import PropTypes from "prop-types"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"

import useDebounce from "src/hooks/use_debounce"

import { queryApi } from "src/utils/api/ApiMethods"

const fetchContactBoatSearch = (
  marinaSlug,
  { contactId, q, extraFields = [] }
) => {
  const params = new URLSearchParams()
  if (contactId) params.append("contact_id", contactId)
  if (q) params.append("q", q)
  if (extraFields.length) params.append("include", extraFields.join(","))
  return queryApi(
    `/manage/${marinaSlug}/contact_boats/search?${params.toString()}`
  )
}

const boatLabel = (boat) => boat?.displayName || boat?.name || ""

const ContactBoatAutocomplete = ({
  name,
  id,
  onAddBoat,
  required = true,
  allowBoatFirst = false,
  disabled = false,
  extraFields = [],
}) => {
  const { marinaSlug } = useMarina()
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()
  const contactId = watch("contact_id")
  const formBoatId = watch(name)

  const [inputValue, setInputValue] = useState("")
  // Object form of the selected boat — Form.Combobox needs the whole item, not
  // just the id, so the input can render the label and the equality check works.
  const [selectedBoat, setSelectedBoat] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSetSearchQuery] = useDebounce(setSearchQuery)
  // Track previous contactId so we only reset when staff explicitly switch
  // from one set contact to another. Going from empty → set (e.g. boat-first
  // marina-wide pick that fills both fields) must not wipe the boat.
  const prevContactIdRef = useRef(contactId)

  const extraFieldsKey = [...extraFields].sort().join(",")

  const contactScoped = useQuery(
    [
      "contactBoatAutocomplete",
      "scoped",
      marinaSlug,
      contactId,
      extraFieldsKey,
    ],
    () => fetchContactBoatSearch(marinaSlug, { contactId, extraFields }),
    {
      enabled: Boolean(contactId),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  const marinaWide = useQuery(
    [
      "contactBoatAutocomplete",
      "marina",
      marinaSlug,
      searchQuery,
      extraFieldsKey,
    ],
    () => fetchContactBoatSearch(marinaSlug, { q: searchQuery, extraFields }),
    {
      enabled: !contactId && allowBoatFirst && searchQuery.trim().length >= 2,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  // Reset selection only when staff switch contacts (prev was set, now
  // different). Empty → set is a fresh selection (e.g. boat-first picked) and
  // must keep the boat we just chose.
  useEffect(() => {
    const prev = prevContactIdRef.current
    prevContactIdRef.current = contactId
    if (prev && prev !== contactId) {
      setValue(name, "")
      setValue("contact_boat", {})
      setSelectedBoat(null)
      setInputValue("")
      setSearchQuery("")
    }
  }, [contactId, name, setValue])

  const allScopedBoats = useMemo(
    () => contactScoped.data?.data ?? [],
    [contactScoped.data?.data]
  )
  const marinaBoats = useMemo(
    () => marinaWide.data?.data ?? [],
    [marinaWide.data?.data]
  )

  // For contact-scoped: filter the preloaded list client-side by typed input.
  // For marina-wide: server already returned filtered results for the query.
  const baseItems = contactId ? allScopedBoats : marinaBoats

  // Mirror the form value into the displayed selection:
  // - When form arrives with a contact_boat_id (e.g. editing a record) and
  //   the contact's boats finish loading, surface the matching boat's label.
  // - When an external consumer clears contact_boat_id (e.g. the picker
  //   chip's remove button), drop the local selection so the input resets.
  useEffect(() => {
    if (!formBoatId) {
      if (selectedBoat) {
        setSelectedBoat(null)
        setInputValue("")
      }
      return
    }
    if (selectedBoat || baseItems.length === 0) return
    const match = baseItems.find((b) => String(b.id) === String(formBoatId))
    if (match) {
      setSelectedBoat(match)
      setInputValue(boatLabel(match))
    }
  }, [baseItems, formBoatId, selectedBoat])

  // Always keep the currently-selected boat in items so base-ui's Combobox
  // doesn't drop the value when the items list churns (e.g. after a boat-first
  // pick switches the query from marina-wide to contact-scoped before the
  // contact's boats finish loading).
  const sourceItems = useMemo(() => {
    if (selectedBoat && !baseItems.some((b) => b.id === selectedBoat.id)) {
      return [selectedBoat, ...baseItems]
    }
    return baseItems
  }, [baseItems, selectedBoat])

  const filteredItems = useMemo(() => {
    if (!contactId) return sourceItems
    const term = inputValue.trim().toLowerCase()
    if (!term) return sourceItems
    return sourceItems.filter((b) => boatLabel(b).toLowerCase().includes(term))
  }, [sourceItems, inputValue, contactId])

  const isFetching = contactId
    ? contactScoped.isFetching
    : marinaWide.isFetching
  const isDisabled = disabled || (!contactId && !allowBoatFirst)

  const placeholder = (() => {
    if (!contactId && !allowBoatFirst) return "Select a customer first..."
    if (!contactId) return "Search boats by name..."
    if (isFetching) return "Loading boats..."
    if (allScopedBoats.length === 0) return "No boats on file"
    return "Search or select a boat..."
  })()

  const handleInputValueChange = useCallback(
    (nextValue, eventDetails) => {
      const reason = eventDetails?.reason
      const userTyped = reason === "input-change" || reason === "input-paste"
      const userCleared =
        reason === "input-clear" || (userTyped && nextValue === "")

      setInputValue(nextValue)

      if (userTyped && !contactId) {
        debouncedSetSearchQuery(nextValue)
      }

      if (userCleared && selectedBoat) {
        setSelectedBoat(null)
        setValue(name, "")
      }
    },
    [contactId, debouncedSetSearchQuery, name, selectedBoat, setValue]
  )

  return (
    <Controller
      name={name}
      control={control}
      rules={
        !isDisabled && required ? { required: "Please select a boat" } : {}
      }
      render={({ field: { onChange } }) => (
        <div>
          <Form.Combobox
            id={id}
            items={sourceItems}
            filteredItems={filteredItems}
            value={selectedBoat}
            onValueChange={(boat) => {
              if (!boat) {
                setSelectedBoat(null)
                setValue("contact_boat", {}, { shouldDirty: true })
                onChange("")
                return
              }
              setSelectedBoat(boat)
              setInputValue(boatLabel(boat))
              onChange(boat.id)
              setValue("contact_boat", boat, { shouldDirty: true })
              if (!contactId && boat?.contact?.id) {
                setValue("contact_id", boat.contact.id, { shouldDirty: true })
                setValue("contact", boat.contact, { shouldDirty: true })
              }
            }}
            onInputValueChange={handleInputValueChange}
            inputValue={inputValue}
            itemToStringLabel={boatLabel}
            isItemEqualToValue={(item, val) => item?.id === val?.id}
            isLoading={isFetching}
            placeholder={placeholder}
            disabled={isDisabled}
            hasErrors={!!errors?.[name]}
            renderOption={({ option }) => (
              <div className="flex flex-col py-0.5">
                <span className="font-medium text-gray-900">
                  {boatLabel(option)}
                </span>
                {option.contact?.name && (
                  <span className="text-xs text-gray-500">
                    {option.contact.name}
                  </span>
                )}
              </div>
            )}
            noResultsContent={isFetching ? "Loading…" : "No matching boats"}
          />
          {onAddBoat && contactId && (
            <div className="mt-1">
              <Button variant="link" small onClick={() => onAddBoat()}>
                + Add a new boat
              </Button>
            </div>
          )}
          {errors?.[name] && <Form.Error>{errors[name].message}</Form.Error>}
        </div>
      )}
    />
  )
}

ContactBoatAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  onAddBoat: PropTypes.func,
  required: PropTypes.bool,
  allowBoatFirst: PropTypes.bool,
  disabled: PropTypes.bool,
  extraFields: PropTypes.arrayOf(PropTypes.string),
}

export default ContactBoatAutocomplete
